import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Activity, CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/diagnostics");
      setData(data);
    } catch (err) {
      toast.error("فشل التحميل: " + (err?.response?.data?.detail || "خطأ"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) return (
    <div className="text-center py-20 text-foreground/50">
      <Loader2 className="h-6 w-6 animate-spin inline" /> جاري التحميل...
    </div>
  );

  const env = data.environment;
  const critical = [
    { key: "Stripe live-mode enabled", ok: env.live_mode_enabled, hint: "يجب أن يكون LIVE_MODE_ENABLED=true حتى تُعالج مدفوعات Stripe الحقيقية." },
    { key: "Stripe مفتاح API", ok: data.stripe.configured && data.stripe.mode === "LIVE", hint: data.stripe.mode === "TEST" ? "أنت تستخدم مفاتيح TEST." : (!data.stripe.configured ? "مفتاح Stripe غير مضبوط." : "") },
    { key: "Stripe Webhook Secret", ok: data.stripe.webhook_secret_configured, hint: "بدونه سيقبل الخادم أي webhook — يجب تعيين STRIPE_WEBHOOK_SECRET من إعدادات Stripe." },
    { key: "Resend (بريد)", ok: data.resend.configured, hint: "بدون RESEND_API_KEY لن ترسل رسائل البريد." },
    { key: "Twilio WhatsApp", ok: data.twilio_whatsapp.configured, hint: "تأكد من TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_WHATSAPP_FROM." },
  ];

  return (
    <div className="space-y-6" data-testid="admin-diagnostics">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">فحص الاتصالات</h1>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh-diag">
          <RefreshCw className="h-4 w-4 me-1" /> تحديث
        </Button>
      </div>

      <p className="text-sm text-foreground/60">
        هذه الصفحة تفحص إعدادات البيئة الحالية (المعاينة أو الإنتاج). إن كانت النتائج مختلفة في الإنتاج،
        استخدمها لتشخيص لماذا لا تصل البريد أو الواتساب.
      </p>

      {/* Critical checklist */}
      <Card className="p-4 space-y-2" data-testid="diag-critical">
        <h2 className="text-sm font-semibold text-foreground/80 mb-2">الفحوصات الحرجة</h2>
        {critical.map((c) => (
          <div key={c.key} className="flex items-start gap-2 py-1.5 border-b border-foreground/5 last:border-0">
            {c.ok
              ? <CheckCircle2 className="h-4 w-4 text-green-700 shrink-0 mt-0.5" />
              : <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.key}</div>
              {!c.ok && c.hint && <div className="text-[11px] text-red-700 mt-0.5">{c.hint}</div>}
            </div>
            <Badge variant={c.ok ? "default" : "destructive"} className={c.ok ? "bg-green-100 text-green-800 text-[10px]" : "text-[10px]"}>
              {c.ok ? "OK" : "احتاج ضبط"}
            </Badge>
          </div>
        ))}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4 space-y-2" data-testid="diag-env">
          <h3 className="text-sm font-semibold">البيئة</h3>
          <KV k="Live mode" v={String(env.live_mode_enabled)} bad={!env.live_mode_enabled} />
          <KV k="Frontend URL" v={env.frontend_url || "(غير محدد)"} />
          <KV k="CORS Origins" v={env.cors_origins} />
        </Card>

        <Card className="p-4 space-y-2" data-testid="diag-stripe">
          <h3 className="text-sm font-semibold">Stripe</h3>
          <KV k="الوضع" v={data.stripe.mode} bad={data.stripe.mode !== "LIVE"} />
          <KV k="Webhook Secret" v={data.stripe.webhook_secret_configured ? data.stripe.webhook_secret_prefix : "غير مضبوط"} bad={!data.stripe.webhook_secret_configured} />
        </Card>

        <Card className="p-4 space-y-2" data-testid="diag-resend">
          <h3 className="text-sm font-semibold">Resend (بريد)</h3>
          <KV k="API Key" v={data.resend.configured ? data.resend.api_key_prefix : "غير مضبوط"} bad={!data.resend.configured} />
        </Card>

        <Card className="p-4 space-y-2" data-testid="diag-twilio">
          <h3 className="text-sm font-semibold">Twilio WhatsApp</h3>
          <KV k="Account SID" v={data.twilio_whatsapp.account_sid_prefix || "غير مضبوط"} bad={!data.twilio_whatsapp.account_sid_prefix} />
          <KV k="Auth Token" v={data.twilio_whatsapp.auth_token_prefix || "غير مضبوط"} bad={!data.twilio_whatsapp.auth_token_prefix} />
          <KV k="From number" v={data.twilio_whatsapp.from_number || "غير مضبوط"} bad={!data.twilio_whatsapp.from_number} />
        </Card>
      </div>

      <Card className="p-4 space-y-2" data-testid="diag-wa-templates">
        <h3 className="text-sm font-semibold">قوالب واتساب المسجّلة</h3>
        <p className="text-xs text-foreground/60">
          {data.whatsapp_templates.with_content_sid} من أصل {data.whatsapp_templates.total} قالب مربوط بـ ContentSid صالح.
        </p>
        <div className="grid gap-1 text-xs">
          {data.whatsapp_templates.list.map((t) => (
            <div key={t.key} className="flex items-center gap-2 py-1">
              {t.has_content_sid && !t.disabled
                ? <CheckCircle2 className="h-3 w-3 text-green-700" />
                : <AlertTriangle className="h-3 w-3 text-amber-600" />}
              <code dir="ltr" className="font-mono">{t.key}</code>
              {t.disabled && <Badge variant="destructive" className="text-[9px]">معطّل</Badge>}
              {!t.has_content_sid && <span className="text-red-700 text-[10px]">— ContentSid ناقص</span>}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-2" data-testid="diag-webhooks">
        <h3 className="text-sm font-semibold">Webhook events (آخر 7 أيام)</h3>
        <p className="text-xs text-foreground/60">إجمالي: {data.webhook_events_last_7d.total}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
          {Object.entries(data.webhook_events_last_7d.by_status).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 bg-foreground/5 rounded px-2 py-1">
              <span dir="ltr" className="font-mono">{k}</span>
              <span className="font-semibold">{v}</span>
            </div>
          ))}
        </div>
        <details className="mt-3">
          <summary className="text-xs cursor-pointer text-foreground/60">حسب النوع</summary>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs mt-2">
            {Object.entries(data.webhook_events_last_7d.by_type).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 bg-foreground/5 rounded px-2 py-1">
                <span dir="ltr" className="font-mono truncate">{k}</span>
                <span className="font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </details>
      </Card>

      <CustomerTrace />
    </div>
  );
}

function CustomerTrace() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [trace, setTrace] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setTrace(null);
    try {
      const { data } = await api.get("/admin/diagnostics/trace", { params: { email: email.trim() } });
      setTrace(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل البحث");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="diag-trace">
      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2">
          🔎 تتبّع عميل — لماذا لم يستلم بريدًا أو واتساب؟
        </h3>
        <p className="text-xs text-foreground/60 mt-1">
          أدخل بريد العميل لرؤية كل ما يعرفه النظام عنه: هل وصل الشراء، هل أُنشئ رابط تفعيل، هل نجح إرسال البريد/الواتساب،
          هل هو ملغى الاشتراك، وأسباب محتملة لأي فشل.
        </p>
      </div>
      <form onSubmit={run} className="flex gap-2 max-w-lg">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          dir="ltr"
          placeholder="customer@example.com"
          data-testid="trace-email"
        />
        <Button type="submit" disabled={busy} data-testid="btn-trace">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تتبّع"}
        </Button>
      </form>

      {trace && (
        <div className="space-y-3 pt-2" data-testid="trace-result">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
            <h4 className="text-xs font-semibold text-amber-900 mb-1">التشخيص</h4>
            <ul className="text-xs text-amber-900/90 space-y-1 list-disc ps-5">
              {trace.diagnosis.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>

          <div className="grid gap-2 md:grid-cols-2 text-xs">
            <TraceSection title={`المشتريات (${trace.purchases.length})`}>
              {trace.purchases.length === 0 && <em className="text-foreground/50">لا شيء.</em>}
              {trace.purchases.map((p, i) => (
                <div key={i} className="border-b border-foreground/10 py-1">
                  <div><strong>{p.amount} {p.currency}</strong> — {p.payment_status} / {p.access_status || "بانتظار"}</div>
                  <div className="text-[10px] text-foreground/50" dir="ltr">
                    {p.created_at?.slice(0, 19)} — invite_generated={String(!!p.invite_generated)}
                  </div>
                </div>
              ))}
            </TraceSection>

            <TraceSection title={`روابط التفعيل (${trace.invites.length})`}>
              {trace.invites.length === 0 && <em className="text-foreground/50">لا شيء.</em>}
              {trace.invites.map((iv, i) => (
                <div key={i} className="border-b border-foreground/10 py-1">
                  <div>الحالة: <Badge variant="outline" className="text-[9px]">{iv.status_ar || iv.status}</Badge></div>
                  <div className="text-[10px] text-foreground/50" dir="ltr">
                    ينتهي {iv.expires_at?.slice(0, 10)}
                  </div>
                </div>
              ))}
            </TraceSection>

            <TraceSection title={`سجل البريد (${trace.email_log.length})`}>
              {trace.email_log.length === 0 && <em className="text-foreground/50">لم يُحاول النظام إرسال أي بريد.</em>}
              {trace.email_log.map((l, i) => (
                <div key={i} className="border-b border-foreground/10 py-1">
                  <div>
                    <Badge className={`text-[9px] ${l.status === "sent" ? "bg-green-100 text-green-800" : l.status === "failed" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-800"}`}>
                      {l.status}
                    </Badge>{" "}
                    <span dir="ltr">{l.template_key || "?"}</span>
                  </div>
                  {l.error && <div className="text-[10px] text-red-700" dir="ltr">{l.error}</div>}
                  <div className="text-[10px] text-foreground/50" dir="ltr">{l.sent_at?.slice(0, 19)}</div>
                </div>
              ))}
            </TraceSection>

            <TraceSection title={`سجل واتساب (${trace.whatsapp_log.length})`}>
              {trace.whatsapp_log.length === 0 && <em className="text-foreground/50">لا يوجد سجل رسائل واتساب صادرة.</em>}
              {trace.whatsapp_log.map((l, i) => (
                <div key={i} className="border-b border-foreground/10 py-1">
                  <div>
                    <Badge className={`text-[9px] ${l.status === "delivered" || l.status === "sent" || l.status === "queued" ? "bg-green-100 text-green-800" : l.status === "failed" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-800"}`}>
                      {l.status}
                    </Badge>{" "}
                    {l.template_key && <span dir="ltr">{l.template_key}</span>}
                  </div>
                  {l.error && <div className="text-[10px] text-red-700" dir="ltr">{l.error}</div>}
                  <div className="text-[10px] text-foreground/50" dir="ltr">{(l.sent_at || l.created_at)?.slice(0, 19)}</div>
                </div>
              ))}
            </TraceSection>

            <TraceSection title={`Stripe Webhook events (${(trace.stripe_webhook_events || []).length})`}>
              {(trace.stripe_webhook_events || []).length === 0 && (
                <em className="text-foreground/50">لم يستلم الخادم أي webhook من Stripe لهذا العميل — تحقق من إعداد endpoint في لوحة Stripe.</em>
              )}
              {(trace.stripe_webhook_events || []).map((ev, i) => (
                <div key={i} className="border-b border-foreground/10 py-1">
                  <div>
                    <Badge className={`text-[9px] ${ev.status === "ok" ? "bg-green-100 text-green-800" : ev.status === "skipped_live_mode_disabled" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-800"}`}>
                      {ev.status}
                    </Badge>{" "}
                    <span dir="ltr">{ev.event_type}</span>
                  </div>
                  <div className="text-[10px] text-foreground/50" dir="ltr">{ev.received_at?.slice(0, 19)}</div>
                </div>
              ))}
            </TraceSection>
          </div>

          <div className="flex flex-wrap gap-3 text-xs pt-2 border-t">
            {trace.unsubscribed && (
              <Badge className="bg-red-100 text-red-800 text-[10px]">🚫 ملغى الاشتراك من البريد</Badge>
            )}
            {trace.user_account && (
              <Badge className="bg-green-100 text-green-800 text-[10px]">✓ لديه حساب مفعّل</Badge>
            )}
            {!trace.user_account && trace.invites.length > 0 && (
              <Badge className="bg-amber-100 text-amber-800 text-[10px]">⏳ رابط تفعيل موجود لكن لم يُفعّل الحساب بعد</Badge>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function TraceSection({ title, children }) {
  return (
    <div className="border rounded-md p-2">
      <div className="text-[11px] font-semibold text-foreground/70 mb-1">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function KV({ k, v, bad }) {
  return (
    <div className="flex justify-between items-center text-xs py-1 border-b border-foreground/5 last:border-0">
      <span className="text-foreground/60">{k}</span>
      <span className={`font-mono truncate max-w-[60%] ${bad ? "text-red-700" : ""}`} dir="ltr" title={v}>{v}</span>
    </div>
  );
}
